import React, { useEffect, useState } from "react";

const FontSettingsEditor = () => {
  const API_URL = "https://eventscons.com/eventbackendnew/api/choose-font";

  const fontOptions = [
    { label: "Arial", value: "Arial", type: "Sans-serif" },
    { label: "Helvetica", value: "Helvetica", type: "Sans-serif" },
    { label: "Roboto", value: "Roboto", type: "Sans-serif" },
    { label: "Open Sans", value: "Open Sans", type: "Sans-serif" },
    { label: "Poppins", value: "Poppins", type: "Sans-serif" },
    { label: "Montserrat", value: "Montserrat", type: "Sans-serif" },
    { label: "Lato", value: "Lato", type: "Sans-serif" },
    { label: "Verdana", value: "Verdana", type: "Sans-serif" },
    { label: "Times New Roman", value: "Times New Roman", type: "Serif" },
    { label: "Georgia", value: "Georgia", type: "Serif" },
  ];

  const weightOptions = [
    { label: "100 → Thin", value: "100" },
    { label: "200 → Extra Light (Ultra Light)", value: "200" },
    { label: "300 → Light", value: "300" },
    { label: "400 → Regular (Normal)", value: "400" },
    { label: "500 → Medium", value: "500" },
    { label: "600 → Semi Bold (Demi Bold)", value: "600" },
    { label: "700 → Bold", value: "700" },
    { label: "800 → Extra Bold (Ultra Bold)", value: "800" },
    { label: "900 → Black (Heavy)", value: "900" },
  ];

  const colorPalette = [
    "#000000",
    "#1f2937",
    "#374151",
    "#6b7280",
    "#9ca3af",
    "#ffffff",
    "#9B1321",
    "#c1121f",
    "#ef4444",
    "#f97316",
    "#f59e0b",
    "#eab308",
    "#22c55e",
    "#10b981",
    "#14b8a6",
    "#06b6d4",
    "#0ea5e9",
    "#3b82f6",
    "#6366f1",
    "#8b5cf6",
    "#ec4899",
  ];

  const sizeOptions = Array.from({ length: 41 }, (_, index) => String(index + 10));

  const [formData, setFormData] = useState({
    font_family: "Poppins",
    font_weight: "700",
    heading_color: "#9B1321",
    paragraph_font_family: "Arial",
    paragraph_font_weight: "400",
    paragraph_color: "#374151",
    font_size: "35",
    paragraph_font_size: "17",
  });

  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchFontSettings();
  }, []);

  const normalizeFontSize = (value, fallback) => {
    if (value === null || value === undefined || value === "") return fallback;
    const parsed = parseInt(String(value).replace("px", "").trim(), 10);
    return Number.isNaN(parsed) ? fallback : String(parsed);
  };

  const normalizeColor = (color, fallback) => {
    if (!color || typeof color !== "string") return fallback;
    const trimmed = color.trim();

    if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed;

    if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
      return (
        "#" +
        trimmed[1] +
        trimmed[1] +
        trimmed[2] +
        trimmed[2] +
        trimmed[3] +
        trimmed[3]
      );
    }

    return fallback;
  };

  const fetchFontSettings = async () => {
    try {
      setFetching(true);
      setError("");

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      const text = await response.text();
      console.log("GET RAW RESPONSE:", text);

      let result = {};
      try {
        result = text ? JSON.parse(text) : {};
      } catch (jsonError) {
        throw new Error("GET API JSON return nahi kar rahi.");
      }

      if (!response.ok) {
        throw new Error(result?.message || "Failed to fetch font settings.");
      }

      if (result?.data) {
        setFormData((prev) => ({
          ...prev,
          font_family: result.data.font_family || prev.font_family,
          font_weight: result.data.font_weight || prev.font_weight,
          heading_color: normalizeColor(result.data.heading_color, prev.heading_color),
          paragraph_font_family:
            result.data.paragraph_font_family || prev.paragraph_font_family,
          paragraph_font_weight:
            result.data.paragraph_font_weight || prev.paragraph_font_weight,
          paragraph_color: normalizeColor(
            result.data.paragraph_color,
            prev.paragraph_color
          ),
          font_size: normalizeFontSize(result.data.font_size, prev.font_size),
          paragraph_font_size: normalizeFontSize(
            result.data.paragraph_font_size,
            prev.paragraph_font_size
          ),
        }));
      }
    } catch (err) {
      console.error("FETCH SETTINGS ERROR:", err);
      setError(err.message || "Failed to fetch font settings.");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "font_size" || name === "paragraph_font_size"
          ? value.replace(/[^\d]/g, "")
          : value,
    }));
  };

  const handleColorPick = (field, color) => {
    setFormData((prev) => ({
      ...prev,
      [field]: color,
    }));
  };

  const validateForm = () => {
    if (!formData.font_family) return "Heading font family is required.";
    if (!formData.font_weight) return "Heading font weight is required.";
    if (!formData.paragraph_font_family) return "Paragraph font family is required.";
    if (!formData.paragraph_font_weight) return "Paragraph font weight is required.";

    if (!formData.font_size || Number(formData.font_size) < 1) {
      return "Heading font size valid hona chahiye.";
    }

    if (
      !formData.paragraph_font_size ||
      Number(formData.paragraph_font_size) < 1
    ) {
      return "Paragraph font size valid hona chahiye.";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setSaving(false);
      return;
    }

    try {
      const payload = {
        font_family: formData.font_family,
        font_weight: String(formData.font_weight),
        heading_color: normalizeColor(formData.heading_color, "#000000"),
        paragraph_font_family: formData.paragraph_font_family,
        paragraph_font_weight: String(formData.paragraph_font_weight),
        paragraph_color: normalizeColor(formData.paragraph_color, "#333333"),
        font_size: Number(formData.font_size),
        paragraph_font_size: Number(formData.paragraph_font_size),
      };

      console.log("POST PAYLOAD:", payload);

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      console.log("POST RAW RESPONSE:", text);

      let result = {};
      try {
        result = text ? JSON.parse(text) : {};
      } catch (jsonError) {
        throw new Error(
          "API JSON return nahi kar rahi. Ho sakta hai CORS, HTML error page, ya server issue ho."
        );
      }

      if (!response.ok) {
        if (result?.errors) {
          const firstError = Object.values(result.errors)?.[0]?.[0];
          throw new Error(firstError || "Validation failed.");
        }

        throw new Error(
          result?.message || `Failed to save settings. Status: ${response.status}`
        );
      }

      setMessage(result?.message || "Font settings saved successfully.");
      await fetchFontSettings();
    } catch (err) {
      console.error("SAVE ERROR:", err);
      setError(err.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const renderPalette = (fieldName, selectedColor) => {
    return (
      <div style={styles.paletteGrid}>
        {colorPalette.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => handleColorPick(fieldName, color)}
            title={color}
            style={{
              ...styles.colorSwatch,
              backgroundColor: color,
              border:
                selectedColor === color
                  ? "3px solid #111827"
                  : color.toLowerCase() === "#ffffff"
                  ? "1px solid #d1d5db"
                  : "1px solid transparent",
              transform: selectedColor === color ? "scale(1.08)" : "scale(1)",
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <section style={styles.pageSection}>
      <div style={styles.pageContainer}>
        {fetching && <p style={styles.infoText}>Loading existing settings...</p>}

        <form onSubmit={handleSubmit} style={styles.formWrap}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Main Heading Fonts</h3>

            <div style={styles.gridTwo}>
              <div style={styles.fieldGroup}>
                <label htmlFor="font_family" style={styles.label}>
                  Heading Font Family
                </label>
                <select
                  id="font_family"
                  name="font_family"
                  value={formData.font_family}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {fontOptions.map((font) => (
                    <option key={font.value} value={font.value}>
                      {font.label} → {font.type}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="font_weight" style={styles.label}>
                  Heading Font Weight
                </label>
                <select
                  id="font_weight"
                  name="font_weight"
                  value={formData.font_weight}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {weightOptions.map((weight) => (
                    <option key={weight.value} value={weight.value}>
                      {weight.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="font_size" style={styles.label}>
                  Heading Font Size
                </label>
                <select
                  id="font_size"
                  name="font_size"
                  value={formData.font_size}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {sizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}px
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Heading Color Palette</label>
              {renderPalette("heading_color", formData.heading_color)}
            </div>

            <div style={styles.colorRow}>
              <div style={styles.fieldGroup}>
                <label htmlFor="heading_color" style={styles.label}>
                  Custom Heading Color
                </label>
                <input
                  id="heading_color"
                  type="color"
                  name="heading_color"
                  value={formData.heading_color}
                  onChange={handleChange}
                  style={styles.colorInput}
                />
              </div>

              <div style={styles.selectedColorBox}>
                <span style={styles.selectedColorLabel}>Selected</span>
                <div
                  style={{
                    ...styles.selectedColorPreview,
                    backgroundColor: formData.heading_color,
                  }}
                />
                <span style={styles.selectedColorValue}>
                  {formData.heading_color}
                </span>
              </div>
            </div>

            <h5 style={styles.previewTitle}>Preview Heading</h5>
            <div style={styles.previewBox}>
              <h1
                style={{
                  margin: 0,
                  fontFamily: formData.font_family,
                  fontWeight: formData.font_weight,
                  color: formData.heading_color,
                  fontSize: Number(formData.font_size),
                  lineHeight: "1.2",
                }}
              >
                This is heading preview text
              </h1>
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>All Paragraph Fonts</h3>

            <div style={styles.gridTwo}>
              <div style={styles.fieldGroup}>
                <label htmlFor="paragraph_font_family" style={styles.label}>
                  Paragraph Font Family
                </label>
                <select
                  id="paragraph_font_family"
                  name="paragraph_font_family"
                  value={formData.paragraph_font_family}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {fontOptions.map((font) => (
                    <option key={font.value} value={font.value}>
                      {font.label} → {font.type}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="paragraph_font_weight" style={styles.label}>
                  Paragraph Font Weight
                </label>
                <select
                  id="paragraph_font_weight"
                  name="paragraph_font_weight"
                  value={formData.paragraph_font_weight}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {weightOptions.map((weight) => (
                    <option key={weight.value} value={weight.value}>
                      {weight.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label htmlFor="paragraph_font_size" style={styles.label}>
                  Paragraph Font Size
                </label>
                <select
                  id="paragraph_font_size"
                  name="paragraph_font_size"
                  value={formData.paragraph_font_size}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {sizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}px
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Paragraph Color Palette</label>
              {renderPalette("paragraph_color", formData.paragraph_color)}
            </div>

            <div style={styles.colorRow}>
              <div style={styles.fieldGroup}>
                <label htmlFor="paragraph_color" style={styles.label}>
                  Custom Paragraph Color
                </label>
                <input
                  id="paragraph_color"
                  type="color"
                  name="paragraph_color"
                  value={formData.paragraph_color}
                  onChange={handleChange}
                  style={styles.colorInput}
                />
              </div>

              <div style={styles.selectedColorBox}>
                <span style={styles.selectedColorLabel}>Selected</span>
                <div
                  style={{
                    ...styles.selectedColorPreview,
                    backgroundColor: formData.paragraph_color,
                  }}
                />
                <span style={styles.selectedColorValue}>
                  {formData.paragraph_color}
                </span>
              </div>
            </div>

            <h5 style={styles.previewTitle}>Preview Text</h5>
            <div style={styles.previewBox}>
              <p
                style={{
                  margin: 0,
                  fontFamily: formData.paragraph_font_family,
                  fontWeight: formData.paragraph_font_weight,
                  color: formData.paragraph_color,
                  fontSize: Number(formData.paragraph_font_size),
                  lineHeight: "1.8",
                }}
              >
                This is paragraph preview text. Use this preview to check paragraph
                font family, paragraph weight, paragraph size, and paragraph color
                before saving settings into the database.
              </p>
            </div>
          </div>

          {message ? <div style={styles.successBox}>{message}</div> : null}
          {error ? <div style={styles.errorBox}>{error}</div> : null}

          <button type="submit" disabled={saving} style={styles.submitBtn}>
            {saving ? "Saving..." : "Save Font Settings"}
          </button>
        </form>
      </div>
    </section>
  );
};

const styles = {
  pageSection: {
    background: "#f8fafc",
    padding: "30px 20px",
    minHeight: "100vh",
  },
  pageContainer: {
    maxWidth: "1100px",
    margin: "0 auto",
  },
  infoText: {
    fontSize: "15px",
    color: "#374151",
    marginBottom: "16px",
    fontWeight: "600",
  },
  formWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  card: {
    background: "#ffffff",
    borderRadius: "20px",
    border: "1px solid #e5e7eb",
    padding: "24px",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
  },
  cardTitle: {
    margin: "0 0 20px 0",
    fontSize: "24px",
    fontWeight: "800",
    color: "#111827",
    textAlign: "left",
  },
  gridTwo: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "18px",
    marginBottom: "18px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#374151",
    textAlign: "left",
  },
  select: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "12px",
    border: "1px solid #d1d5db",
    fontSize: "15px",
    background: "#fff",
    color: "#111827",
    outline: "none",
  },
  paletteGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "6px",
    marginBottom: "18px",
  },
  colorSwatch: {
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  colorRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
    marginBottom: "20px",
  },
  colorInput: {
    width: "90px",
    height: "48px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
  },
  selectedColorBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    borderRadius: "12px",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
  },
  selectedColorLabel: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#6b7280",
  },
  selectedColorPreview: {
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    border: "1px solid #d1d5db",
  },
  selectedColorValue: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#111827",
  },
  previewTitle: {
    margin: "0 0 10px 0",
    fontSize: "16px",
    fontWeight: "700",
    color: "#111827",
  },
  previewBox: {
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: "16px",
    padding: "22px",
  },
  successBox: {
    background: "#ecfdf5",
    color: "#166534",
    border: "1px solid #bbf7d0",
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "14px",
    fontWeight: "700",
  },
  errorBox: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "14px",
    fontWeight: "700",
    whiteSpace: "pre-wrap",
  },
  submitBtn: {
    width: "fit-content",
    padding: "14px 24px",
    border: "none",
    borderRadius: "14px",
    background: "#9B1321",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 12px 20px rgba(155, 19, 33, 0.20)",
    opacity: 1,
    marginBottom:'20px',
  },
};

export default FontSettingsEditor;